import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, ExclamationTriangleIcon, UserGroupIcon, BeakerIcon, PencilSquareIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ActivityDrawer from './ActivityDrawer';
import {
    createBulkAssignments,
    fetchAssignment,
    fetchCompostMaterials,
    fetchCropOptions,
    fetchMaterialLots,
    updateAssignment,
    type ActivityMaterialOption,
    type MaterialLot,
} from '../../utils/activityApi';
import type { AssignmentMaterialPlanRow } from './types';
import { normalizeCropName } from '../../utils/cropCatalog';
import { formatCurrency } from '../../utils/currency';
import { buildRequirementOverrides, parseRequirements } from './reportingRequirements';
import type { ActivityType, Assignment, FarmerOption, FarmField, StaffUser } from './types';
import { useAuth } from '../../contexts/AuthContext';

interface AssignmentFormDrawerProps {
    open: boolean;
    onClose: () => void;
    types: ActivityType[];
    farmers: FarmerOption[];
    fields: FarmField[];
    users: StaffUser[];
    onCreated: () => void;
    assignment?: Assignment | null;
}

interface FormState {
    title: string;
    activity_type_id: string;
    crop_type: string;
    priority: string;
    planned_start_date: string;
    due_date: string;
    planned_quantity: string;
    planned_quantity_unit: string;
    planned_worker_count: string;
    planned_workdays: string;
    planned_hours_per_worker: string;
    planned_wage_per_worker: string;
    material_source: 'manual' | 'compost';
    material_option_id: string;
    material_name: string;
    material_quantity: string;
    material_unit: string;
    work_instructions: string;
    safety_instructions: string;
    assigned_to_user_id: string;
    /** null = inherit the activity type default; true/false = explicit override. */
    requires_before_photo: boolean | null;
    requires_after_photo: boolean | null;
    requires_observations: boolean | null;
    requires_signature: boolean | null;
}

const EMPTY_TEMPLATE_REQUIREMENTS: Record<string, boolean | number> = {};

const initialForm: FormState = {
    title: '',
    activity_type_id: '',
    crop_type: '',
    priority: 'normal',
    planned_start_date: '',
    due_date: '',
    planned_quantity: '',
    planned_quantity_unit: '',
    planned_worker_count: '',
    planned_workdays: '',
    planned_hours_per_worker: '',
    planned_wage_per_worker: '',
    material_source: 'manual',
    material_option_id: '',
    material_name: '',
    material_quantity: '',
    material_unit: '',
    work_instructions: '',
    safety_instructions: '',
    assigned_to_user_id: '',
    requires_before_photo: null,
    requires_after_photo: null,
    requires_observations: null,
    requires_signature: null,
};

const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20';

const labelClass = 'mb-1 block text-xs font-black uppercase tracking-wide text-slate-500';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className={labelClass}>{children}</span>
);

const numOrNull = (value: string): number | null => {
    if (value === '' || value === undefined || value === null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const farmerHasMobileLogin = (farmer: FarmerOption | undefined): boolean => Boolean(farmer && (farmer.user_id || farmer.mobile_login_email));

const dateInputValue = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toISOString().slice(0, 10);
};

const stringValue = (value: unknown): string => (value === undefined || value === null ? '' : String(value));

const fieldCrop = (field?: FarmField): string => normalizeCropName(field?.crop_type || '');

/**
 * `null` when the assignment never pinned this requirement (the manager form
 * should then show "inherit from activity type"); true/false when it did.
 */
const explicitRequirement = (
    requirements: Record<string, any>,
    canonical: string,
    legacy: string,
): boolean | null => {
    if (typeof requirements[canonical] === 'boolean') return requirements[canonical];
    if (typeof requirements[legacy] === 'boolean') return requirements[legacy];
    return null;
};

const AssignmentFormDrawer: React.FC<AssignmentFormDrawerProps> = ({
    open,
    onClose,
    types,
    farmers,
    fields,
    users,
    onCreated,
    assignment,
}) => {
    const { user } = useAuth();
    const currencyCode = user?.tenant?.default_currency || 'RWF';
    const [form, setForm] = useState<FormState>(initialForm);
    const [selectedFieldId, setSelectedFieldId] = useState<string>('');
    const [selectedFarmerIds, setSelectedFarmerIds] = useState<string[]>([]);
    const [fieldSearch, setFieldSearch] = useState('');
    const [farmerSearch, setFarmerSearch] = useState('');
    const [cropOptions, setCropOptions] = useState<string[]>([]);
    const [materialOptions, setMaterialOptions] = useState<ActivityMaterialOption[]>([]);
    const [lots, setLots] = useState<MaterialLot[]>([]);
    const [lotsError, setLotsError] = useState('');
    const [planRows, setPlanRows] = useState<AssignmentMaterialPlanRow[]>([]);
    // True only once the job's existing plan has actually loaded. Without it, an
    // edit must never send an empty `materials` array and silently clear the plan.
    const [planLoaded, setPlanLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const isEdit = Boolean(assignment?.id);
    const locked = isEdit && ['approved', 'rejected', 'cancelled', 'closed'].includes(String(assignment?.status || '').toLowerCase());

    useEffect(() => {
        if (!open) return;
        fetchCropOptions().then(setCropOptions).catch(() => setCropOptions([]));
        fetchCompostMaterials().then(setMaterialOptions).catch(() => setMaterialOptions([]));
        setLotsError('');
        fetchMaterialLots({ include_depleted: 'true' })
            .then(setLots)
            .catch((err) => {
                setLots([]);
                setLotsError(err?.message || 'Material stock is unavailable. Apply migration 079 on the server.');
            });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!assignment) {
            setForm(initialForm);
            setSelectedFieldId('');
            setSelectedFarmerIds([]);
            setFieldSearch('');
            setFarmerSearch('');
            setPlanRows([]);
            setPlanLoaded(false);
            setError('');
            setSuccess('');
            return;
        }
        // Preserve the planned lot ids when editing: the source identity is what
        // links this job to the receipt, stock and farmer report.
        setPlanLoaded(false);
        fetchAssignment(assignment.id)
            .then((detail) => {
                const planned = (detail.materials || [])
                    .filter((row) => row.status !== 'cancelled')
                    .map((row) => ({
                        ...row,
                        material_lot_id: Number(row.material_lot_id),
                        planned_quantity: Number(row.planned_quantity),
                        application_rate: row.application_rate ?? null,
                        application_rate_unit: row.application_rate_unit || null,
                    }));
                setPlanRows(planned);
                setPlanLoaded(true);
            })
            .catch(() => {
                // Keep existing ids out of the save path: an unavailable plan must
                // not be replaced by an empty array.
                setPlanRows([]);
                setPlanLoaded(false);
            });

        const requirements = parseRequirements(assignment.reporting_requirements);
        const farmerId = assignment.farmer_id || assignment.assigned_to_farmer_id;
        setForm({
            title: stringValue(assignment.title),
            activity_type_id: stringValue(assignment.activity_type_id),
            crop_type: stringValue(assignment.crop_type),
            priority: stringValue(assignment.priority || 'normal'),
            planned_start_date: dateInputValue(assignment.planned_start_date),
            due_date: dateInputValue(assignment.due_date),
            planned_quantity: stringValue(assignment.planned_quantity),
            planned_quantity_unit: stringValue(assignment.planned_quantity_unit),
            planned_worker_count: stringValue(assignment.planned_worker_count),
            planned_workdays: stringValue(assignment.planned_workdays),
            planned_hours_per_worker: stringValue(assignment.planned_hours_per_worker),
            planned_wage_per_worker: stringValue(assignment.planned_wage_per_worker),
            material_source: 'manual',
            material_option_id: '',
            material_name: stringValue(assignment.material_name),
            material_quantity: stringValue(assignment.material_quantity),
            material_unit: stringValue(assignment.material_unit),
            work_instructions: stringValue(assignment.work_instructions),
            safety_instructions: stringValue(assignment.safety_instructions),
            assigned_to_user_id: stringValue(assignment.assigned_to_user_id),
            requires_before_photo: explicitRequirement(requirements, 'requires_before_photo', 'before_photo'),
            requires_after_photo: explicitRequirement(requirements, 'requires_after_photo', 'after_photo'),
            requires_observations: explicitRequirement(requirements, 'requires_observations', 'observations'),
            requires_signature: explicitRequirement(requirements, 'requires_signature', 'signature'),
        });
        setSelectedFieldId(assignment.field_plot_id ? String(assignment.field_plot_id) : '');
        setSelectedFarmerIds(farmerId ? [String(farmerId)] : []);
        setFieldSearch('');
        setFarmerSearch('');
        setError('');
        setSuccess('');
    }, [assignment, open]);

    const farmersById = useMemo(
        () => Object.fromEntries(farmers.map((farmer) => [String(farmer.id), farmer])),
        [farmers],
    );

    const selectedField = useMemo(
        () => fields.find((field) => String(field.id) === selectedFieldId),
        [fields, selectedFieldId],
    );

    // Field-first: eligible farmers are the field's membership set (farm_plot_farmers),
    // falling back to the legacy primary owner when memberships have not been written yet.
    const fieldFarmers = useMemo(() => {
        if (!selectedField) return [];
        let rawMembers: unknown = selectedField.farmers;
        if (typeof rawMembers === 'string') {
            try {
                rawMembers = JSON.parse(rawMembers);
            } catch {
                rawMembers = [];
            }
        }
        const members = Array.isArray(rawMembers)
            ? rawMembers
                .map((member) => ({
                    id: Number(member.id),
                    name: member.name || farmersById[String(member.id)]?.full_name || `Farmer ${member.id}`,
                    role_on_field: member.role_on_field,
                    is_primary: member.is_primary,
                }))
                .filter((member) => Number.isInteger(member.id) && member.id > 0)
            : [];
        const legacyId = selectedField.farmer_id ? Number(selectedField.farmer_id) : null;
        if (!legacyId || members.some((member) => member.id === legacyId)) return members;
        const legacy = farmersById[String(legacyId)];
        return [{
            id: legacyId,
            name: legacy?.full_name || selectedField.farmer_name || `Farmer ${legacyId}`,
            role_on_field: 'owner',
            is_primary: true,
        }, ...members];
    }, [selectedField, farmersById]);

    const filteredFields = useMemo(() => {
        const query = fieldSearch.trim().toLowerCase();
        if (!query) return fields;
        return fields.filter((field) =>
            [
                field.name,
                field.plot_code,
                field.field_code,
                field.crop_type,
                field.farmer_name,
                field.primary_farmer_name,
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query)),
        );
    }, [fields, fieldSearch]);

    const eligibleFarmers = useMemo(() => {
        const query = farmerSearch.trim().toLowerCase();
        if (!query) return fieldFarmers;
        return fieldFarmers.filter((farmer) =>
            [farmer.name, farmersById[String(farmer.id)]?.phone, farmersById[String(farmer.id)]?.farmer_code]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(query)),
        );
    }, [fieldFarmers, farmerSearch, farmersById]);

    const staffUsers = useMemo(
        () =>
            users.filter((user) =>
                ['agronomist', 'agronomist_advisor', 'field_facilitator', 'field_scout', 'project_manager', 'tenant_admin', 'business_manager', 'field_manager'].includes(
                    String(user.role || '').toLowerCase(),
                ),
            ),
        [users],
    );

    const reset = () => {
        setForm(initialForm);
        setSelectedFieldId('');
        setSelectedFarmerIds([]);
        setFieldSearch('');
        setFarmerSearch('');
        setError('');
        setSuccess('');
    };

    const close = () => {
        reset();
        onClose();
    };

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleFieldSelect = (fieldId: string) => {
        const field = fields.find((item) => String(item.id) === fieldId);
        setSelectedFieldId(fieldId);
        // Farmers must come from the newly selected field's membership.
        setSelectedFarmerIds([]);
        const crop = fieldCrop(field);
        if (crop) set('crop_type', crop);
    };

    const toggleFarmer = (id: string) => {
        setSelectedFarmerIds((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
        );
    };

    const applyMaterialOption = (optionId: string) => {
        const option = materialOptions.find((item) => item.id === optionId);
        setForm((prev) => ({
            ...prev,
            material_option_id: optionId,
            material_name: option?.name || prev.material_name,
            material_unit: option?.unit || prev.material_unit || 'kg',
        }));
    };

    const lotById = useMemo(
        () => Object.fromEntries(lots.map((lot) => [String(lot.id), lot])),
        [lots],
    );

    const addPlanRow = () => setPlanRows((prev) => [
        ...prev,
        {
            material_lot_id: 0,
            planned_quantity: 0,
            unit: 'kg',
            application_rate: null,
            application_rate_unit: null,
        },
    ]);

    const updatePlanRow = (index: number, patch: Partial<AssignmentMaterialPlanRow>) => {
        setPlanRows((prev) => prev.map((row, rowIndex) => {
            if (rowIndex !== index) return row;
            const next = { ...row, ...patch };
            // Selecting a lot adopts its unit so a plan can never be saved in a
            // unit the stock is not measured in.
            if (patch.material_lot_id !== undefined) {
                const lot = lotById[String(patch.material_lot_id)];
                if (lot) next.unit = lot.unit;
            }
            return next;
        }));
    };

    const removePlanRow = (index: number) => {
        setPlanRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    };

    /** Everything the plan section needs to warn about before saving. */
    const planWarnings = useMemo(() => {
        const warnings: string[] = [];
        const seen = new Set<number>();
        planRows.forEach((row) => {
            const lotId = Number(row.material_lot_id);
            const lot = lotById[String(lotId)];
            if (!lotId || !lot) {
                warnings.push('Choose a material lot for every planned row.');
                return;
            }
            if (seen.has(lotId)) warnings.push(`Lot ${lot.lot_code} is planned twice.`);
            seen.add(lotId);
            const planned = Number(row.planned_quantity);
            if (!Number.isFinite(planned) || planned <= 0) {
                warnings.push(`Give a planned quantity for lot ${lot.lot_code}.`);
            }
            const free = Number(lot.available_quantity) - Number(lot.reserved_quantity);
            if (planned > free) {
                warnings.push(`Lot ${lot.lot_code}: ${planned}${lot.unit} planned but only ${free}${lot.unit} is free after other open jobs.`);
            }
            if (lot.status === 'depleted' || lot.status === 'disputed' || lot.status === 'cancelled') {
                warnings.push(`Lot ${lot.lot_code} is ${lot.status} and cannot be promised.`);
            }
            if (lot.field_restriction_id && selectedFieldId && String(lot.field_restriction_id) !== String(selectedFieldId)) {
                warnings.push(`Lot ${lot.lot_code} is restricted to a different field.`);
            }
            if (row.application_rate !== null && row.application_rate !== undefined
                && String(row.application_rate) !== '' && !(Number(row.application_rate) > 0)) {
                warnings.push(`The application rate for lot ${lot.lot_code} must be greater than zero.`);
            }
        });
        return [...new Set(warnings)];
    }, [planRows, lotById, selectedFieldId]);

    const handleTypeChange = (value: string) => {
        const type = types.find((t) => String(t.id) === value);
        const allowedUnits = type?.operation_template?.quantity_units || [];
        setForm((prev) => ({
            ...prev,
            activity_type_id: value,
            planned_quantity_unit: allowedUnits.length && !allowedUnits.some((unit) => unit.toLowerCase() === prev.planned_quantity_unit.toLowerCase())
                ? allowedUnits[0]
                : prev.planned_quantity_unit,
            // Requirement overrides stay as the manager left them; they no longer
            // track the activity type, so a type change never silently re-pins
            // evidence the manager never chose.
            safety_instructions: type?.safety_instructions ? prev.safety_instructions || type.safety_instructions : prev.safety_instructions,
            work_instructions: type?.work_instructions ? prev.work_instructions || type.work_instructions : prev.work_instructions,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const activityType = types.find((t) => String(t.id) === form.activity_type_id);
        const basePayload: Record<string, any> = {
            title: form.title,
            activity_type_id: form.activity_type_id ? Number(form.activity_type_id) : null,
            activity_type_name: activityType?.name || null,
            crop_type: null,
            season_name: null,
            priority: form.priority || 'normal',
            planned_start_date: form.planned_start_date || null,
            due_date: form.due_date || null,
            planned_quantity: numOrNull(form.planned_quantity),
            planned_quantity_unit: form.planned_quantity_unit || null,
            material_name: form.material_name || null,
            material_quantity: numOrNull(form.material_quantity),
            material_unit: form.material_unit || null,
            work_instructions: form.work_instructions || null,
            safety_instructions: form.safety_instructions || null,
            assigned_to_user_id: form.assigned_to_user_id ? Number(form.assigned_to_user_id) : null,
            // Only what the manager actually changed is written; an untouched
            // requirement keeps inheriting the activity type default.
            reporting_requirements: buildRequirementOverrides(form),
        };
        if (/^(irrigation|watering)$/i.test(activityType?.name || '')) {
            basePayload.reporting_requirements = {
                ...basePayload.reporting_requirements,
                requires_input: false,
                requires_materials: false,
                requires_photo: false,
                requires_gps: false,
                requires_area_completed: false,
                requires_start_time: false,
                requires_end_time: false,
                requires_weather: false,
                requires_completion_notes: false,
                requires_equipment: false,
                requires_observations: false,
                requires_before_photo: false,
                requires_after_photo: false,
                requires_signature: false,
                requires_quantity: false,
            };
        }

        const labourValues = [
            form.planned_worker_count,
            form.planned_workdays,
            form.planned_hours_per_worker,
            form.planned_wage_per_worker,
        ];
        const hasLabourInput = labourValues.some((value) => value.trim() !== '');
        const assignmentHasLabourPlan = Boolean(assignment && [
            assignment.planned_worker_count,
            assignment.planned_workdays,
            assignment.planned_hours_per_worker,
            assignment.planned_wage_per_worker,
        ].some((value) => value !== undefined && value !== null));
        if (hasLabourInput || (isEdit && assignmentHasLabourPlan)) {
            basePayload.labour_plan = {
                expected_worker_count: form.planned_worker_count,
                planned_workdays: form.planned_workdays,
                expected_hours_per_worker: form.planned_hours_per_worker,
                expected_wage_per_worker: form.planned_wage_per_worker,
            };
        }

        // The lot plan is the contract the stock ledger, the farmer report and
        // the review all point at. On edit it is always sent - an empty array is
        // how a manager clears the plan (and the legacy material_lot_id with it).
        const materialsPayload = planRows
            .filter((row) => Number(row.material_lot_id) > 0)
            .map((row) => ({
                material_lot_id: Number(row.material_lot_id),
                planned_quantity: Number(row.planned_quantity),
                unit: row.unit || lotById[String(row.material_lot_id)]?.unit || 'kg',
                application_rate: row.application_rate === null || row.application_rate === undefined
                    || String(row.application_rate) === '' ? null : Number(row.application_rate),
                application_rate_unit: row.application_rate_unit || null,
            }));
        // An edit sends the plan only when it was actually loaded; "clear the
        // plan" is expressed by removing every row after a successful load.
        if (materialsPayload.length || (isEdit && planLoaded)) {
            basePayload.materials = materialsPayload;
        }

        if (!basePayload.title && !basePayload.activity_type_id) {
            setError('Add a title or choose an activity type before saving.');
            return;
        }
        if (basePayload.due_date && basePayload.planned_start_date && basePayload.due_date < basePayload.planned_start_date) {
            setError('Due date cannot be before the planned start date.');
            return;
        }
        if (!selectedFieldId) {
            setError('Select a mapped farm field first — every assignment is tied to one real field boundary.');
            return;
        }
        if (selectedFarmerIds.length === 0) {
            setError(isEdit ? 'Select a farmer from this field before saving the assignment.' : 'Select at least one farmer assigned to this field.');
            return;
        }
        const noLoginFarmers = selectedFarmerIds
            .map((farmerId) => farmersById[farmerId])
            .filter((farmer) => !farmerHasMobileLogin(farmer));
        if (noLoginFarmers.length > 0) {
            const names = noLoginFarmers.map((farmer) => farmer?.full_name || `Farmer ${farmer?.id}`).join(', ');
            setError(
                `"${names}" ${noLoginFarmers.length === 1 ? 'has no mobile login linked' : 'have no mobile login linked'}, so ${noLoginFarmers.length === 1 ? 'they' : 'they'} cannot receive assignments on the mobile app. Link a mobile account first (or pick a different farmer).`,
            );
            return;
        }
        if (isEdit && locked) {
            setError(`This assignment is ${assignment?.status} and cannot be edited.`);
            return;
        }
        if (planWarnings.some((warning) => warning.includes('Choose a material lot')
            || warning.includes('planned twice')
            || warning.includes('Give a planned quantity')
            || warning.includes('cannot be promised')
            || warning.includes('restricted to a different field')
            || warning.includes('must be greater than zero'))) {
            setError(planWarnings[0]);
            return;
        }

        setSaving(true);
        try {
            // Field-first: every assignment in the batch shares the SAME field_plot_id.
            const crop = fieldCrop(selectedField) || normalizeCropName(form.crop_type);
            const farmerEntries = selectedFarmerIds.map((farmerId) => ({
                farmer_id: Number(farmerId),
                assigned_to_farmer_id: Number(farmerId),
                field_plot_id: Number(selectedFieldId),
                crop_type: crop || null,
            }));
            if (isEdit && assignment?.id) {
                await updateAssignment(assignment.id, { ...basePayload, ...farmerEntries[0] });
                setSuccess('Assignment updated. The work queue and mobile task list will refresh with the new assignment details.');
            } else {
                await createBulkAssignments({ ...basePayload, farmers: farmerEntries });
                setSuccess(
                    `${farmerEntries.length} assignment${farmerEntries.length === 1 ? '' : 's'} created${activityType ? ` (${activityType.name})` : ''}. Publish them to put them on the operational queue.`,
                );
            }
            onCreated();
            setTimeout(() => {
                close();
            }, 900);
        } catch (err: any) {
            setError(err?.message || 'Failed to create assignment.');
        } finally {
            setSaving(false);
        }
    };

    const selectedMaterial = materialOptions.find((option) => option.id === form.material_option_id);

    const plannedWorkerCount = Number(form.planned_worker_count);
    const plannedWorkdays = Number(form.planned_workdays);
    const plannedWagePerWorker = Number(form.planned_wage_per_worker);
    const plannedLabourEstimate = Number.isInteger(plannedWorkerCount) && plannedWorkerCount > 0
        && Number.isFinite(plannedWorkdays) && plannedWorkdays > 0
        && Number.isFinite(plannedWagePerWorker) && plannedWagePerWorker > 0
        ? plannedWorkerCount * plannedWorkdays * plannedWagePerWorker
        : null;

    const selectedType = types.find((t) => String(t.id) === form.activity_type_id);
    const templateFields = selectedType?.operation_template?.planning_fields || [];
    const templateRequirements = selectedType?.operation_template?.reporting_requirements || EMPTY_TEMPLATE_REQUIREMENTS;
    const showQuantityFields = !selectedType || selectedType.requires_materials || selectedType.requires_input
        || templateFields.includes('quantity') || templateFields.includes('materials');
    const showMaterialFields = !selectedType || selectedType.requires_materials || selectedType.requires_input
        || templateFields.includes('materials');
    const showPlanSection = showQuantityFields || showMaterialFields;
    const showInstructionsSection = !selectedType
        || selectedType.requires_equipment
        || selectedType.requires_materials
        || selectedType.requires_input
        || Boolean(selectedType.operation_template)
        || Boolean(form.work_instructions || form.safety_instructions);
    const showReportingSection = !selectedType
        || selectedType.requires_photo
        || selectedType.requires_gps
        || selectedType.requires_before_photo
        || selectedType.requires_after_photo
        || selectedType.requires_observations
        || selectedType.requires_signature
        || Object.values(templateRequirements).some((value) => value === true);
    const typeRequirementChips = useMemo(() => {
        if (!selectedType) return [];
        const labels: string[] = [];
        if (selectedType.requires_gps) labels.push('GPS');
        if (selectedType.requires_start_time) labels.push('Start time');
        if (selectedType.requires_end_time) labels.push('End time');
        if (selectedType.requires_photo) labels.push('Photo evidence');
        if (selectedType.requires_before_photo) labels.push('Before photo');
        if (selectedType.requires_after_photo) labels.push('After photo');
        if (selectedType.requires_observations) labels.push('Observations');
        if (selectedType.requires_signature) labels.push('Farmer signature');
        if (selectedType.requires_area_completed) labels.push('Area completed');
        if (selectedType.requires_materials) labels.push('Materials used');
        if (selectedType.requires_input) labels.push('Inventory input');
        if (selectedType.requires_equipment) labels.push('Equipment');
        if (selectedType.requires_weather) labels.push('Weather');
        if (templateRequirements.requires_gps && !selectedType.requires_gps) labels.push('GPS');
        if (templateRequirements.requires_start_time && !selectedType.requires_start_time) labels.push('Start time');
        if (templateRequirements.requires_end_time && !selectedType.requires_end_time) labels.push('End time');
        if (templateRequirements.requires_photo && !selectedType.requires_photo) labels.push('Photo evidence');
        if (templateRequirements.requires_before_photo && !selectedType.requires_before_photo) labels.push('Before photo');
        if (templateRequirements.requires_after_photo && !selectedType.requires_after_photo) labels.push('After photo');
        if (templateRequirements.requires_observations && !selectedType.requires_observations) labels.push('Observations');
        if (templateRequirements.requires_signature && !selectedType.requires_signature) labels.push('Farmer confirmation');
        if (templateRequirements.requires_area_completed && !selectedType.requires_area_completed) labels.push('Area completed');
        if (templateRequirements.requires_materials && !selectedType.requires_materials) labels.push('Materials used');
        if (templateRequirements.requires_input && !selectedType.requires_input) labels.push('Inventory input');
        if (templateRequirements.requires_equipment && !selectedType.requires_equipment) labels.push('Equipment');
        if (templateRequirements.requires_weather && !selectedType.requires_weather) labels.push('Weather');
        if (templateRequirements.requires_quantity) labels.push('Completed quantity');
        return labels;
    }, [selectedType, templateRequirements]);

    return (
        <ActivityDrawer
            open={open}
            onClose={close}
            width="2xl"
            title={isEdit ? 'Edit Assignment' : 'Create Assignment'}
            subtitle={
                isEdit
                    ? 'Update the activity plan, farmer, field, staff assignee, materials, and reporting requirements.'
                    : 'Plan a field activity, assign farmers and staff, and define reporting requirements.'
            }
            footer={
                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={close}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="assignment-form"
                        disabled={saving || locked}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-black text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {isEdit ? <PencilSquareIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create assignment'}
                    </button>
                </div>
            }
        >
            <form id="assignment-form" onSubmit={handleSubmit} className="space-y-6">
                {locked && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        This assignment is {assignment?.status}. Terminal assignments are kept as audit records and cannot be edited.
                    </div>
                )}
                {error && (
                    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        {error}
                    </div>
                )}
                {success && (
                    <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                        {success}
                    </div>
                )}

                <section>
                    <h3 className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Identity</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <FieldLabel>Title</FieldLabel>
                            <input
                                className={inputClass}
                                value={form.title}
                                onChange={(e) => set('title', e.target.value)}
                                placeholder="e.g. Fertilizer application - Block A"
                            />
                        </div>
                        <div>
                            <FieldLabel>Activity type</FieldLabel>
                            <select
                                className={inputClass}
                                value={form.activity_type_id}
                                onChange={(e) => handleTypeChange(e.target.value)}
                            >
                                <option value="">Select type</option>
                                {types.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {selectedType && (
                                <div className="mt-2 rounded-lg border border-brand-blue/20 bg-brand-blue/5 px-3 py-2">
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                        This type requires
                                    </p>
                                    {typeRequirementChips.length > 0 ? (
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {typeRequirementChips.map((label) => (
                                                <span
                                                    key={label}
                                                    className="rounded-full bg-brand-blue/10 px-2 py-0.5 text-[11px] font-bold text-brand-blue"
                                                >
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="mt-1 text-xs font-semibold text-slate-500">No capture fields required — plan, assign, and publish only.</p>
                                    )}
                                    {selectedType.operation_template && (
                                        <div className="mt-3 space-y-2 border-t border-brand-blue/10 pt-2">
                                            <p className="text-xs font-semibold text-slate-700">
                                                {selectedType.operation_template.work_prompt}
                                            </p>
                                            <p className="text-xs text-slate-600">
                                                Safety: {selectedType.operation_template.safety_guidance}
                                            </p>
                                            <p className="text-[11px] font-bold text-slate-500">
                                                Completion: {selectedType.operation_template.completion_criteria.join(' · ')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <FieldLabel>Priority</FieldLabel>
                            <select
                                className={inputClass}
                                value={form.priority}
                                onChange={(e) => set('priority', e.target.value)}
                            >
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>
                    </div>
                </section>

                <section>
                    <h3 className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Field &amp; farmers</h3>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <MapPinIcon className="h-5 w-5 text-emerald-700" />
                                <p className="text-sm font-black text-slate-900">
                                    1 · Choose the mapped farm field
                                </p>
                            </div>
                            {selectedFieldId && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedFieldId('');
                                        setSelectedFarmerIds([]);
                                    }}
                                    className="text-xs font-black text-slate-400 hover:text-slate-700"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <input
                            className={inputClass}
                            value={fieldSearch}
                            onChange={(e) => setFieldSearch(e.target.value)}
                            placeholder="Search fields by name, code, crop, or farmer..."
                        />
                        <p className="mt-2 text-xs font-semibold text-slate-500">
                            One real boundary = one assignment target. Farmers must already be assigned to this field in Farm Maps.
                        </p>
                        <div className="mt-3 grid max-h-56 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
                            {filteredFields.map((field) => {
                                const id = String(field.id);
                                const checked = selectedFieldId === id;
                                return (
                                    <button
                                        type="button"
                                        key={field.id}
                                        onClick={() => handleFieldSelect(id)}
                                        className={`rounded-xl border px-3 py-2 text-left transition ${
                                            checked
                                                ? 'border-emerald-300 bg-white shadow-sm ring-2 ring-emerald-100'
                                                : 'border-slate-100 bg-white hover:border-emerald-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <span className="min-w-0">
                                                <span className="block truncate text-sm font-black text-slate-800">
                                                    {field.name || field.plot_code || `Field ${field.id}`}
                                                </span>
                                                <span className="mt-0.5 block truncate text-xs text-slate-400">
                                                    {[field.plot_code || field.field_code, field.crop_type ? normalizeCropName(field.crop_type) : '', Number(field.area_hectares || 0) > 0 ? `${Number(field.area_hectares).toFixed(2)} ha` : '']
                                                        .filter(Boolean)
                                                        .join(' · ') || `Field ${field.id}`}
                                                </span>
                                            </span>
                                            <input
                                                type="radio"
                                                checked={checked}
                                                readOnly
                                                className="mt-1 h-4 w-4 shrink-0 accent-emerald-600"
                                            />
                                        </div>
                                        <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                                            {Number(field.farmer_count || (Array.isArray(field.farmers) ? field.farmers.length : 0))} farmer
                                            {(field.farmer_count || (Array.isArray(field.farmers) ? field.farmers.length : 0)) === 1 ? '' : 's'} assigned
                                            {field.primary_farmer_name || field.farmer_name ? ` · ${field.primary_farmer_name || field.farmer_name}` : ''}
                                        </p>
                                    </button>
                                );
                            })}
                            {filteredFields.length === 0 && (
                                <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm font-semibold text-slate-400">
                                    No mapped fields match your search. Create one in Farm Maps first.
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedFieldId && (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <UserGroupIcon className="h-5 w-5 text-emerald-700" />
                                    <p className="text-sm font-black text-slate-900">
                                        2 · Farmers on {selectedField?.name || `field ${selectedFieldId}`}
                                        {' — '}
                                        {selectedFarmerIds.length} selected
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedFarmerIds([])}
                                    className="text-xs font-black text-slate-400 hover:text-slate-700"
                                >
                                    Clear
                                </button>
                            </div>
                            <input
                                className={inputClass}
                                value={farmerSearch}
                                onChange={(e) => setFarmerSearch(e.target.value)}
                                placeholder="Search farmers assigned to this field..."
                            />
                            {isEdit && (
                                <p className="mt-2 text-xs font-semibold text-slate-500">
                                    Edit mode updates one assignment. Select a different farmer from this field if the task must move.
                                </p>
                            )}
                            <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
                                {eligibleFarmers.map((farmer) => {
                                    const id = String(farmer.id);
                                    const checked = selectedFarmerIds.includes(id);
                                    const option = farmersById[id];
                                    return (
                                        <button
                                            type="button"
                                            key={farmer.id}
                                            onClick={() => toggleFarmer(id)}
                                            className={`rounded-xl border px-3 py-2 text-left transition ${
                                                checked
                                                    ? 'border-emerald-300 bg-white shadow-sm ring-2 ring-emerald-100'
                                                    : 'border-slate-100 bg-white hover:border-emerald-200'
                                            } ${option && !farmerHasMobileLogin(option) ? 'opacity-70' : ''}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="min-w-0">
                                                    <span className="flex items-center gap-1.5">
                                                        <span className="block truncate text-sm font-black text-slate-800">{farmer.name}</span>
                                                        {farmer.is_primary && (
                                                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                                                Primary
                                                            </span>
                                                        )}
                                                        {option && !farmerHasMobileLogin(option) && (
                                                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                                                No mobile login
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="mt-0.5 block truncate text-xs text-slate-400">
                                                        {option?.farmer_code || option?.phone || option?.email || `Farmer ${farmer.id}`}
                                                    </span>
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    readOnly
                                                    className="mt-1 h-4 w-4 shrink-0 accent-emerald-600"
                                                />
                                            </div>
                                            <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 capitalize">
                                                {farmer.role_on_field || 'member'} on field
                                            </p>
                                        </button>
                                    );
                                })}
                                {eligibleFarmers.length === 0 && (
                                    <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-sm font-semibold text-slate-400">
                                        No farmers are assigned to this field yet. Add them in Farm Maps first.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <div>
                            <FieldLabel>Crop source</FieldLabel>
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-900">
                                {form.crop_type ? normalizeCropName(form.crop_type) : 'Select a mapped field to auto-fill crop'}
                            </div>
                            <p className="mt-1 text-xs font-semibold text-slate-400">
                                Crop is inherited from the selected Farm Maps field. Use the fallback only when a task is not tied to a mapped field.
                            </p>
                        </div>
                        <div>
                            <FieldLabel>Fallback crop</FieldLabel>
                            <input
                                className={inputClass}
                                value={form.crop_type}
                                onChange={(e) => set('crop_type', e.target.value)}
                                list="activity-crop-options"
                                placeholder="Only needed without a mapped field"
                            />
                            <datalist id="activity-crop-options">
                                {cropOptions.map((crop) => (
                                    <option key={crop} value={crop} />
                                ))}
                            </datalist>
                        </div>
                        <div>
                            <FieldLabel>Planned start date</FieldLabel>
                            <input
                                type="date"
                                className={inputClass}
                                value={form.planned_start_date}
                                onChange={(e) => set('planned_start_date', e.target.value)}
                            />
                        </div>
                        <div>
                            <FieldLabel>Due date</FieldLabel>
                            <input
                                type="date"
                                className={inputClass}
                                value={form.due_date}
                                onChange={(e) => set('due_date', e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                {showPlanSection && (
                    <section>
                        <h3 className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Plan &amp; materials</h3>
                        <div className="grid gap-4 sm:grid-cols-3">
                            {showQuantityFields && (
                                <>
                                    <div>
                                        <FieldLabel>Planned quantity</FieldLabel>
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className={inputClass}
                                            value={form.planned_quantity}
                                            onChange={(e) => set('planned_quantity', e.target.value)}
                                            placeholder="e.g. 120"
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel>Quantity unit</FieldLabel>
                                        {selectedType?.operation_template?.quantity_units.length ? (
                                            <select
                                                className={inputClass}
                                                value={form.planned_quantity_unit}
                                                onChange={(e) => set('planned_quantity_unit', e.target.value)}
                                            >
                                                {selectedType.operation_template.quantity_units.map((unit) => (
                                                    <option key={unit} value={unit}>{unit}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                className={inputClass}
                                                value={form.planned_quantity_unit}
                                                onChange={(e) => set('planned_quantity_unit', e.target.value)}
                                                placeholder="kg"
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                            {showMaterialFields && (
                                <div className="sm:col-span-3">
                            <FieldLabel>Material</FieldLabel>
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <BeakerIcon className="h-5 w-5 text-emerald-700" />
                                    {(['manual', 'compost'] as const).map((source) => (
                                        <button
                                            key={source}
                                            type="button"
                                            onClick={() =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    material_source: source,
                                                    material_option_id: source === 'manual' ? '' : prev.material_option_id,
                                                }))
                                            }
                                            className={`rounded-full px-3 py-1.5 text-xs font-black capitalize transition ${
                                                form.material_source === source
                                                    ? 'bg-emerald-600 text-white'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {source === 'manual' ? 'Manual input' : 'From compost'}
                                        </button>
                                    ))}
                                </div>
                                {form.material_source === 'compost' && (
                                    <div className="mb-4">
                                        <select
                                            className={inputClass}
                                            value={form.material_option_id}
                                            onChange={(e) => applyMaterialOption(e.target.value)}
                                        >
                                            <option value="">Select compost batch or material source</option>
                                            {materialOptions.map((option) => (
                                                <option key={option.id} value={option.id}>
                                                    {option.name}
                                                    {option.subtitle ? ` · ${option.subtitle}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        {selectedMaterial && (
                                            <p className="mt-1 text-xs font-semibold text-slate-400">
                                                Linked source: {selectedMaterial.source.replace(/_/g, ' ')}
                                                {selectedMaterial.availableQuantity !== undefined
                                                    ? ` • ${selectedMaterial.availableQuantity.toLocaleString()} ${selectedMaterial.unit}`
                                                    : ''}
                                            </p>
                                        )}
                                        {materialOptions.length === 0 && (
                                            <p className="mt-1 text-xs font-semibold text-amber-600">
                                                No compost materials are available yet. You can switch to manual input.
                                            </p>
                                        )}
                                    </div>
                                )}
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <input
                                        className={inputClass}
                                        value={form.material_name}
                                        onChange={(e) => set('material_name', e.target.value)}
                                        placeholder="e.g. NPK 17-17-17"
                                        readOnly={form.material_source === 'compost' && Boolean(form.material_option_id)}
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        className={inputClass}
                                        value={form.material_quantity}
                                        onChange={(e) => set('material_quantity', e.target.value)}
                                        placeholder="Qty"
                                    />
                                    <input
                                        className={inputClass}
                                        value={form.material_unit}
                                        onChange={(e) => set('material_unit', e.target.value)}
                                        placeholder="Unit (kg)"
                                    />
                                </div>

                                {/* Tracked lot plan: stable ids, real stock, one rate per lot. */}
                                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                                    <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <p className="text-xs font-black uppercase tracking-wide text-slate-500">Tracked compost / fertiliser lots</p>
                                            <p className="mt-1 text-xs font-semibold text-slate-500">
                                                These rows reserve real lot stock, follow the job to the farmer and are what the
                                                review deducts. The agronomist must enter and approve the product-specific rate;
                                                nothing here invents a fertiliser dose.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addPlanRow}
                                            disabled={lots.length === 0}
                                            className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-black text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-100 disabled:opacity-50"
                                        >
                                            <PlusIcon className="h-4 w-4" /> Add lot
                                        </button>
                                    </div>

                                    {lotsError && (
                                        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700">
                                            {lotsError}
                                        </p>
                                    )}

                                    {planRows.length === 0 ? (
                                        <p className="text-xs font-semibold text-slate-400">
                                            No tracked lot planned. Add one when this job consumes compost, manure or fertiliser
                                            that was received into stock.
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {planRows.map((row, index) => {
                                                const lot = lotById[String(row.material_lot_id)];
                                                const free = lot
                                                    ? Number(lot.available_quantity) - Number(lot.reserved_quantity)
                                                    : null;
                                                const restricted = Boolean(lot?.field_restriction_id && selectedFieldId
                                                    && String(lot.field_restriction_id) !== String(selectedFieldId));
                                                return (
                                                    <div key={`${row.material_lot_id || 'new'}-${index}`} className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200">
                                                        <div className="grid gap-3 sm:grid-cols-4">
                                                            <label className="block">
                                                                <span className={labelClass}>Lot</span>
                                                                <select
                                                                    className={inputClass}
                                                                    value={row.material_lot_id ? String(row.material_lot_id) : ''}
                                                                    onChange={(e) => updatePlanRow(index, { material_lot_id: Number(e.target.value) })}
                                                                >
                                                                    <option value="">Select a lot</option>
                                                                    {lots
                                                                        .filter((option) => option.status !== 'cancelled' || String(option.id) === String(row.material_lot_id))
                                                                        .map((option) => (
                                                                            <option key={option.id} value={option.id}>
                                                                                {option.lot_code} · {option.product_name} · {option.unit}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                            </label>
                                                            <label className="block">
                                                                <span className={labelClass}>Planned qty ({row.unit || lot?.unit || 'kg'})</span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="any"
                                                                    className={inputClass}
                                                                    value={row.planned_quantity || ''}
                                                                    onChange={(e) => updatePlanRow(index, { planned_quantity: Number(e.target.value) })}
                                                                />
                                                            </label>
                                                            <label className="block">
                                                                <span className={labelClass}>Application rate</span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="any"
                                                                    className={inputClass}
                                                                    value={row.application_rate ?? ''}
                                                                    onChange={(e) => updatePlanRow(index, {
                                                                        application_rate: e.target.value === '' ? null : Number(e.target.value),
                                                                    })}
                                                                    placeholder="e.g. 2"
                                                                />
                                                            </label>
                                                            <div className="flex items-end gap-2">
                                                                <label className="block flex-1">
                                                                    <span className={labelClass}>Rate unit</span>
                                                                    <input
                                                                        className={inputClass}
                                                                        value={row.application_rate_unit || ''}
                                                                        onChange={(e) => updatePlanRow(index, { application_rate_unit: e.target.value || null })}
                                                                        placeholder="kg/ha"
                                                                    />
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removePlanRow(index)}
                                                                    className="mb-0.5 rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                                                                    aria-label="Remove material lot"
                                                                >
                                                                    <XMarkIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {lot && (
                                                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500">
                                                                <span>{lot.source_type === 'farm_produced' ? 'Farm-produced' : `Purchased${lot.supplier_name ? ` · ${lot.supplier_name}` : ''}`}</span>
                                                                <span>·</span>
                                                                <span>{lot.product_type.replace(/_/g, ' ')}</span>
                                                                <span>·</span>
                                                                <span className="font-black text-slate-700">{free}{lot.unit} free</span>
                                                                <span>·</span>
                                                                <span>payment: {lot.payment_status}</span>
                                                                {lot.expiry_date && <span>· expires {lot.expiry_date}</span>}
                                                                {restricted && (
                                                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 font-black text-amber-700">
                                                                        Restricted to another field
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {planWarnings.length > 0 && (
                                        <ul className="mt-3 space-y-1">
                                            {planWarnings.map((warning) => (
                                                <li key={warning} className="text-xs font-bold text-amber-700">{warning}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                            )}
                    </div>
                </section>
                )}

                <section>
                    <h3 className="mb-1 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Planned labour</h3>
                    <p className="mb-3 text-xs text-slate-500">Planning estimate only. Attendance, earned wages and actual payments are recorded separately.</p>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <FieldLabel>Expected workers</FieldLabel>
                            <input
                                type="number"
                                min="1"
                                max="500"
                                step="1"
                                className={inputClass}
                                value={form.planned_worker_count}
                                onChange={(e) => set('planned_worker_count', e.target.value)}
                                placeholder="e.g. 4"
                            />
                        </div>
                        <div>
                            <FieldLabel>Workdays</FieldLabel>
                            <input
                                type="number"
                                min="0.01"
                                max="365"
                                step="any"
                                className={inputClass}
                                value={form.planned_workdays}
                                onChange={(e) => set('planned_workdays', e.target.value)}
                                placeholder="e.g. 2"
                            />
                        </div>
                        <div>
                            <FieldLabel>Hours per worker / day</FieldLabel>
                            <input
                                type="number"
                                min="0.01"
                                max="24"
                                step="any"
                                className={inputClass}
                                value={form.planned_hours_per_worker}
                                onChange={(e) => set('planned_hours_per_worker', e.target.value)}
                                placeholder="Optional"
                            />
                        </div>
                        <div>
                            <FieldLabel>Daily wage per worker</FieldLabel>
                            <input
                                type="number"
                                min="0.01"
                                max="1000000000"
                                step="any"
                                className={inputClass}
                                value={form.planned_wage_per_worker}
                                onChange={(e) => set('planned_wage_per_worker', e.target.value)}
                                placeholder="e.g. 1,500"
                            />
                            <p className="mt-1 text-[11px] text-slate-500">Amounts in {currencyCode}.</p>
                        </div>
                    </div>
                    {plannedLabourEstimate !== null && (
                        <p className="mt-3 text-sm font-bold text-slate-700">
                            Planned labour estimate per assignment: {formatCurrency(plannedLabourEstimate, currencyCode, { decimals: 2 })}
                        </p>
                    )}
                </section>

                {showInstructionsSection && (
                <section>
                    <h3 className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Instructions</h3>
                    <div className="space-y-4">
                        <div>
                            <FieldLabel>Work instructions</FieldLabel>
                            <textarea
                                className={`${inputClass} min-h-20`}
                                value={form.work_instructions}
                                onChange={(e) => set('work_instructions', e.target.value)}
                                placeholder="Step-by-step guidance for the field team…"
                            />
                        </div>
                        <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
                            <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 accent-emerald-700"
                                checked={Boolean(form.safety_instructions.trim())}
                                onChange={(event) => set(
                                    'safety_instructions',
                                    event.target.checked
                                        ? selectedType?.operation_template?.safety_guidance
                                            || selectedType?.safety_instructions
                                            || 'Follow the operation guidance and use appropriate protection.'
                                        : '',
                                )}
                            />
                            <span>
                                <span className="block font-semibold">Include standard safety reminder</span>
                                {form.safety_instructions.trim() && (
                                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                                        {form.safety_instructions}
                                    </span>
                                )}
                            </span>
                        </label>
                    </div>
                </section>
                )}

                <section>
                    <h3 className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">Assignee &amp; reporting</h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <FieldLabel>Assign to staff</FieldLabel>
                            <select
                                className={inputClass}
                                value={form.assigned_to_user_id}
                                onChange={(e) => set('assigned_to_user_id', e.target.value)}
                            >
                                <option value="">Not assigned to staff</option>
                                {staffUsers.map((user) => (
                                    <option key={user.id} value={user.id}>
                                        {user.full_name} · {String(user.role).replace(/_/g, ' ')}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1.5 text-xs leading-5 text-slate-500">
                                Optional: name one staff member for this assignment. Farmer recipients are selected above; the standing agronomist team for a field is managed separately in Maps.
                            </p>
                        </div>
                        {showReportingSection && (
                        <div className="sm:col-span-2">
                            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-500">
                                Reporting requirements{selectedType ? ' — the job inherits its type and operation-template rules unless you pin a value here' : ''}
                            </p>
                            <div className="grid gap-2 sm:grid-cols-2">
                                {(
                                    [
                                        ['requires_before_photo', 'Before-activity photo'],
                                        ['requires_after_photo', 'After-activity photo'],
                                        ['requires_observations', 'Sample-point observations'],
                                        ['requires_signature', 'Farmer confirmation'],
                                    ] as const
                                ).map(([key, label]) => {
                                    const typeDefault = Boolean(selectedType?.[key] || templateRequirements[key]);
                                    return (
                                        <label key={key} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                                            <span>
                                                {label}
                                                <span className="block text-xs text-slate-400">
                                                    {form[key] === null
                                                        ? `Inherited: ${typeDefault ? 'required' : 'not required'}`
                                                        : 'Pinned on this assignment'}
                                                </span>
                                            </span>
                                            <select
                                                value={form[key] === null ? 'inherit' : form[key] ? 'on' : 'off'}
                                                onChange={(e) => set(key, e.target.value === 'inherit' ? null : e.target.value === 'on')}
                                                className="rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:border-brand-blue"
                                            >
                                                <option value="inherit">Inherit</option>
                                                <option value="on">Required</option>
                                                <option value="off">Not required</option>
                                            </select>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        )}
                    </div>
                </section>
            </form>
        </ActivityDrawer>
    );
};

export default AssignmentFormDrawer;
